import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import {
  TrendingUp, TrendingDown, Wallet, Loader2,
  ArrowUpRight, ArrowDownRight, Minus,
} from "lucide-react";

const EUR = (n) =>
  new Intl.NumberFormat("ca-ES", { style: "currency", currency: "EUR",
    maximumFractionDigits: 0 }).format(Number(n) || 0);

const fmtMonth = (lang) => (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  return new Date(Number(y), Number(m) - 1, 1)
    .toLocaleDateString(lang === "es" ? "es-ES" : "ca-ES",
      { month: "short", year: "2-digit" });
};

export default function ProfitWidget() {
  const { lang } = useI18n();
  const tr = (ca, es) => (lang === "es" ? es : ca);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const m = fmtMonth(lang);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/dashboard/profit-summary");
        if (alive) setData(r.data);
      } catch {
        /* silent */
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="card-surface p-5 text-center text-slate-500">
        <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" />
        <p className="text-sm">{tr("Carregant…", "Cargando…")}</p>
      </div>
    );
  }
  if (!data) return null;

  const inc = data.income?.total || 0;
  const exp = data.expense?.total || 0;
  const net = data.net || 0;
  const positive = net >= 0;
  const empty = inc === 0 && exp === 0;
  const deltaPct = data.net_delta_pct;
  const deltaUp = (data.net_delta || 0) > 0;

  // Sparkline bars: paired income / expense for last 6 months
  const series = data.rolling_6m || [];
  const max = Math.max(
    1, ...series.map((s) => Math.max(s.income || 0, s.expense || 0))
  );

  return (
    <div className="card-surface overflow-hidden" data-testid="profit-widget">
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Wallet size={16} className={positive ? "text-emerald-300" : "text-rose-300"} />
          <h2 className="font-display text-sm font-semibold tracking-tight text-white">
            {tr("Benefici net del mes", "Beneficio neto del mes")}
          </h2>
          <span className="text-[10px] uppercase tracking-wider text-slate-500">
            {m(data.month)}
          </span>
        </div>
        {!empty && deltaPct !== null && deltaPct !== undefined && Number.isFinite(deltaPct) && (
          <div className={`text-[11px] flex items-center gap-1 px-2 py-1 rounded-full border
            ${deltaUp
              ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200"
              : "bg-rose-500/10 border-rose-500/30 text-rose-200"}`}
            data-testid="profit-delta"
          >
            {deltaUp ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
            {deltaPct > 0 ? "+" : ""}{deltaPct.toFixed(1)}% {tr("vs mes ant.", "vs mes ant.")}
          </div>
        )}
      </div>

      {empty ? (
        <div className="p-6 text-center text-slate-500 text-sm">
          <Wallet className="w-8 h-8 mx-auto mb-2 text-slate-600" />
          {tr("Cap moviment aquest mes encara.", "Sin movimientos este mes todavía.")}
        </div>
      ) : (
        <div className="p-5 space-y-4">
          {/* The big number */}
          <div className="flex items-end justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">
                {positive
                  ? tr("Benefici net", "Beneficio neto")
                  : tr("Pèrdua neta", "Pérdida neta")}
              </div>
              <div className={`font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1
                ${positive ? "text-emerald-300" : "text-rose-300"}`}
                data-testid="profit-net"
              >
                {positive ? "+" : ""}{EUR(net)}
              </div>
            </div>
          </div>

          {/* Income vs expense bars */}
          <div className="space-y-2.5" data-testid="profit-bars">
            <Row
              label={tr("Ingressos", "Ingresos")}
              value={inc}
              max={Math.max(inc, exp)}
              color="emerald"
              count={data.income?.count}
              icon={<TrendingUp size={11} />}
              tr={tr}
            />
            <Row
              label={tr("Despeses", "Gastos")}
              value={exp}
              max={Math.max(inc, exp)}
              color="rose"
              count={data.expense?.count}
              icon={<TrendingDown size={11} />}
              tr={tr}
            />
          </div>

          {/* 6-month comparative bars */}
          {series.length > 1 && (
            <div className="pt-3 border-t border-white/5">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
                {tr("Últims 6 mesos", "Últimos 6 meses")}
              </div>
              <div className="flex items-end gap-2 h-20" data-testid="profit-sparkline">
                {series.map((s) => {
                  const hIn = Math.max(2, ((s.income || 0) / max) * 80);
                  const hEx = Math.max(2, ((s.expense || 0) / max) * 80);
                  const isCurrent = s.month === data.month;
                  return (
                    <div
                      key={s.month}
                      className="flex-1 flex flex-col items-center gap-1 group"
                      title={`${m(s.month)} · ${tr("Net", "Net")}: ${EUR(s.net || 0)}`}
                    >
                      <div className="flex items-end gap-0.5 w-full h-20">
                        <div
                          className={`flex-1 rounded-t ${
                            isCurrent ? "bg-emerald-400" : "bg-emerald-500/60"
                          }`}
                          style={{ height: `${hIn}px` }}
                        />
                        <div
                          className={`flex-1 rounded-t ${
                            isCurrent ? "bg-rose-400" : "bg-rose-500/60"
                          }`}
                          style={{ height: `${hEx}px` }}
                        />
                      </div>
                      <span className={`text-[9px] font-mono ${
                        isCurrent ? "text-white" : "text-slate-600"
                      }`}>
                        {m(s.month)}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-end gap-3 text-[10px] text-slate-500 mt-2">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded bg-emerald-400" />
                  {tr("Ingressos", "Ingresos")}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded bg-rose-400" />
                  {tr("Despeses", "Gastos")}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, max, color, count, icon, tr }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  const colorMap = {
    emerald: "bg-gradient-to-r from-emerald-500 to-emerald-400",
    rose: "bg-gradient-to-r from-rose-500 to-rose-400",
  };
  return (
    <div className="text-xs">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <span className={color === "emerald" ? "text-emerald-300" : "text-rose-300"}>
            {icon}
          </span>
          <span className="text-slate-300 font-medium">{label}</span>
          {count !== undefined && (
            <span className="text-slate-600 text-[10px]">
              ({count} {tr("fact.", "fact.")})
            </span>
          )}
        </div>
        <span className="font-mono text-slate-200 font-semibold">
          {EUR(value)}
        </span>
      </div>
      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
        <div className={`h-full ${colorMap[color]}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
